/**
 * TAPE LIGHTS (2026-11-04, Brandon lighting Santiago: "This is a MUST. We need a tape lighting
 * feature for lighting under cabinets, shelving, toe kicks, etc."). docs/MODELER.md "TAPE LIGHTS".
 *
 * A tape light is a FIXTURE (kind `tapeLight`) HOSTED ON A CASEWORK BOX (`hostId` = the box).
 * It stores only WHERE on the box it runs — `tapePlace` (under the case, in the toe kick, on top
 * as a cove, under a shelf of an open box), how far back from the front (`tapeSetbackIn`), how far
 * short of each end (`tapeEndInsetIn`), and which shelf (`tapeShelf`). Its line in the world is
 * DERIVED from the box every resolve (`tapeLightRun`), so it follows the box through every move,
 * re-width, re-layout and restyle with no re-seating, and it goes when the box is deleted (the
 * generic hosted-element cascade). doc.ts stamps the run on the resolved element as `__tape` plus a
 * mid-point `path` anchor and the box's `rotationDeg`, so everything that reads a light's anchor
 * (tags, warnings, switch legs, picking) works unchanged.
 *
 * Box frame (casework/generate3d.ts, the objectKinds placement frame): anchor at the box's BACK
 * centre, local x along the width (centred), local y from the back (0) to the front (depth), z up
 * from the box's level. Uppers start at their type's Mount Height.
 */
import type { Pt2 } from './types'
import { boxCarcassBottomFt, CASEWORK_TOE_FT } from './caseworkAppliances'

export const TAPE_PLACES = [
  { value: 'under', label: 'Under the cabinet' },
  { value: 'toe', label: 'Toe kick' },
  { value: 'above', label: 'Top of the cabinet (cove)' },
  { value: 'shelf', label: 'Under a shelf' },
] as const
export type TapePlace = (typeof TAPE_PLACES)[number]['value']

/** the casework prototype's toe-kick recess (bim-geometry CW_TOE_D) and shelf stock (CW_SHELF_T) */
const TOE_RECESS_FT = 0.25
const SHELF_T_FT = 0.0625
/** a tape channel: 1/2" wide, 1/4" deep */
export const TAPE_WIDTH_FT = 0.5 / 12
export const TAPE_DEPTH_FT = 0.25 / 12

/** default setback from the front (inches) per place — behind the light rail under an upper,
 *  just inside the toe face in a toe kick, near the front edge of a shelf; a cove sits 2" off the
 *  back (wall) and is measured from there */
export const TAPE_DEFAULT_SETBACK_IN: Record<TapePlace, number> = { under: 2, toe: 0.5, above: 2, shelf: 1.5 }
export const TAPE_DEFAULT_END_INSET_IN = 1

type BoxLike = {
  params: Record<string, unknown>
  typeParams: Record<string, unknown>
}

const num = (v: unknown, d: number) => (typeof v === 'number' && isFinite(v) ? v : d)

/** the box's vertical facts the tape reads (feet, above the box's level) */
export function boxVerticals(box: BoxLike): { style: string; z0: number; czb: number; zTop: number; toe: boolean; open: boolean; depth: number; width: number } {
  const tp = box.typeParams
  const style = String(tp.style || 'base')
  const h = (box.params.heightFt as number | null | undefined) ?? (tp.heightFt as number | undefined) ?? 3
  const z0 = style === 'upper' ? num(tp.mountHeightFt, 4.5) || 4.5 : 0
  const hasBase = style === 'base' || style === 'tall' || (style === 'upper' && tp.__rowBaseMolding === true)
  const baseStyle = String(tp.baseStyle || (tp.toeKick === false ? 'flush' : 'toe'))
  const czb = z0 + (hasBase && box.params.runMass !== true ? boxCarcassBottomFt(tp) : 0)
  return {
    style, z0, czb, zTop: z0 + h,
    toe: hasBase && box.params.runMass !== true && (baseStyle === 'toe' || baseStyle === 'legs'),
    open: String(tp.frontConfig || 'doors') === 'open',
    depth: num(tp.depthFt, 2) || 2,
    width: num(box.params.widthFt, 2) || 2,
  }
}

/** the shelves of an open box, lowest first: each shelf's UNDERSIDE (feet above the level) —
 *  the casework prototype's own spacing (protos.ts, cfg 'open') */
export function openBoxShelfUndersides(box: BoxLike): number[] {
  const v = boxVerticals(box)
  if (!v.open) return []
  const t = SHELF_T_FT
  const inner = (v.zTop - t) - (v.czb + t)
  const sc = Number(box.typeParams.shelfCount)
  const n = sc < 0 ? 0 : sc > 0 ? Math.round(sc) : Math.max(1, Math.round(inner / 1.2) - 1)
  const out: number[] = []
  for (let i = 1; i <= n; i++) out.push(v.czb + t + (inner * i) / (n + 1))
  return out
}

/** which places a box offers (the placement tool and the palette list only these) */
export function tapePlacesFor(box: BoxLike): TapePlace[] {
  const v = boxVerticals(box)
  const out: TapePlace[] = []
  // under the case: an upper, or anything that floats clear of the floor
  if (v.style === 'upper' || v.czb > 0.5) out.push('under')
  if (v.toe) out.push('toe')
  if (v.style === 'upper' || v.style === 'tall') out.push('above')
  if (openBoxShelfUndersides(box).length) out.push('shelf')
  return out
}

/** the place a new tape on this box takes when none is picked */
export function defaultTapePlace(box: BoxLike): TapePlace | null {
  const places = tapePlacesFor(box)
  const v = boxVerticals(box)
  const pref: TapePlace[] = v.open ? ['shelf', 'under', 'toe', 'above']
    : v.style === 'upper' ? ['under', 'above'] : v.style === 'tall' ? ['toe', 'above'] : ['toe', 'under']
  return pref.find((p) => places.includes(p)) ?? places[0] ?? null
}

export interface TapeRun {
  /** world ends of the tape (plan) */
  a: Pt2
  b: Pt2
  /** world mid-point (the light's anchor) */
  mid: Pt2
  /** the tape's face height above the box's level (feet) — the face the light leaves */
  z: number
  lengthFt: number
  /** the box's rotation (deg) — the tape runs along the box's local x */
  rotDeg: number
  /** which way the light leaves the tape */
  facing: 'down' | 'up'
  place: TapePlace
}

/**
 * The tape's run on its box, or null when the box cannot hold it there (a toe tape on a box with
 * no toe kick, a shelf tape on a box with no shelves, a box too narrow for its end insets).
 */
export function tapeLightRun(box: BoxLike & { params: { path?: unknown; rotationDeg?: unknown } }, tape: Record<string, unknown>): TapeRun | null {
  const anchor = (box.params.path as { nodes?: { pt: Pt2 }[] } | null | undefined)?.nodes?.[0]?.pt
  if (!anchor) return null
  const place = String(tape.tapePlace || '') as TapePlace
  if (!tapePlacesFor(box).includes(place)) return null
  const v = boxVerticals(box)
  const inset = Math.max(0, num(tape.tapeEndInsetIn, TAPE_DEFAULT_END_INSET_IN)) / 12
  const L = v.width - 2 * inset
  if (L < 1 / 12) return null
  const sbIn = tape.tapeSetbackIn
  const setback = (typeof sbIn === 'number' && isFinite(sbIn) ? Math.max(0, sbIn) : TAPE_DEFAULT_SETBACK_IN[place]) / 12
  let y: number, z: number, facing: 'down' | 'up' = 'down'
  if (place === 'under') { y = v.depth - setback; z = v.czb }
  else if (place === 'toe') { y = v.depth - TOE_RECESS_FT - setback; z = Math.min(v.czb, CASEWORK_TOE_FT) }
  else if (place === 'above') { y = setback; z = v.zTop; facing = 'up' }
  else {
    const shelves = openBoxShelfUndersides(box)
    const i = Math.max(1, Math.min(shelves.length, Math.round(num(tape.tapeShelf, 1)))) - 1
    y = v.depth - setback; z = shelves[i]
  }
  y = Math.max(TAPE_WIDTH_FT / 2, Math.min(v.depth - TAPE_WIDTH_FT / 2, y))
  const rotDeg = num(box.params.rotationDeg, 0)
  const r = (rotDeg * Math.PI) / 180, c = Math.cos(r), s = Math.sin(r)
  const at = (lx: number, ly: number): Pt2 => [anchor[0] + lx * c - ly * s, anchor[1] + lx * s + ly * c]
  return { a: at(-L / 2, y), b: at(L / 2, y), mid: at(0, y), z, lengthFt: L, rotDeg, facing, place }
}

/** the run stamped on a resolved tape light by doc.ts (null when its box can't hold it) */
export function tapeRunOf(el: { params: Record<string, unknown> }): TapeRun | null {
  const t = el.params.__tape
  return t && typeof t === 'object' && Array.isArray((t as TapeRun).a) ? (t as TapeRun) : null
}

/** total tape length (feet) of a set of resolved tape lights */
export function tapeTotalFt(els: { params: Record<string, unknown> }[]): number {
  return els.reduce((s, e) => s + (tapeRunOf(e)?.lengthFt ?? 0), 0)
}
